import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { obliczRabatKoszyka } from '../utils/discountEngine';

const CartContext = createContext(null);

// ---------------------------------------------------------------------------
// BLOKADA TOWARU BEZ CENY (dokladnie 0.00)
//
// Decyzja biznesowa wlasciciela (2026-08): gratis/probka/promocja nigdy za
// 0.00, minimum 0.01 zl; towar z cena 0.00 nie wchodzi do koszyka; rabat nie
// moze sprowadzic pozycji do 0.00. Protokol XML (pxml.txt) NIE stawia warunku
// > 0 - to nie jest wymog drukarki. Kaucja 0.00 jest legalna i nie podlega
// tej regule.
//
// Zakres blokady:
//   - blokujemy WYLACZNIE cene rowna dokladnie 0.00,
//   - pozycje za 0.01 sa DOZWOLONE (i zalecane zamiast zera),
//   - kaucje (typ_produktu = 'kaucja', stawka VAT -2, is_deposit) maja wlasne
//     reguly i NIE podlegaja tej blokadzie,
//   - reczna edycja ceny przez kasjerke pozostaje mozliwa (min. 0.01 zl).
// ---------------------------------------------------------------------------

export const ZERO_PRICE_BLOCK_MESSAGE =
  'towar nie ma ceny (0,00 zl). Uzupelnij cennik - towar nie moze byc sprzedany za 0,00 zl (minimum 0,01 zl).';

export const ZERO_PRICE_WARNING_MESSAGE =
  'Cena 0,00 zl jest niedozwolona - pozycja musi kosztowac co najmniej 0,01 zl.';

// Kaucja / pozycja kaucyjna - rozpoznawana po kilku polach, bo rozne zrodla
// (katalog, szkic, transakcja z zamowienia) nazywaja stawke VAT inaczej.
export const isDepositLikeItem = (item) => {
  if (!item) return false;
  if (item.is_deposit === true || item.is_deposit === 1) return true;
  if (item.typ_produktu === 'kaucja') return true;
  const vat = parseFloat(item.stawka_vat !== undefined && item.stawka_vat !== null
    ? item.stawka_vat
    : item.vat);
  return vat === -2;
};

// Cena produktu z katalogu - kolejnosc pol identyczna jak w addToCart,
// zeby blokada patrzyla dokladnie na te cene, ktora trafilaby do koszyka.
export const resolveProductPrice = (product) => {
  if (!product) return 0;
  const raw = product.has_special_price && product.special_price_brutto
    ? product.special_price_brutto
    : (product.default_price_brutto || product.price || product.cena || 0);
  const parsed = parseFloat(raw);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100) / 100;
};

// Cena pozycji juz bedacej w koszyku (szkic, transakcja z zamowienia).
// Tu pierwszenstwo ma pole price - moze byc recznie zmienione przez kasjerke.
export const resolveCartItemPrice = (item) => {
  if (!item) return 0;
  let raw = 0;
  if (item.price !== undefined && item.price !== null) raw = item.price;
  else if (item.cena !== undefined && item.cena !== null) raw = item.cena;
  else if (item.default_price_brutto !== undefined && item.default_price_brutto !== null) raw = item.default_price_brutto;
  const parsed = parseFloat(raw);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100) / 100;
};

export const isZeroPricedProduct = (product) =>
  !isDepositLikeItem(product) && resolveProductPrice(product) === 0;

export const isZeroPricedCartItem = (item) =>
  !isDepositLikeItem(item) && resolveCartItemPrice(item) === 0;

// Nazwa do komunikatu dla kasjerki
export const itemLabel = (item) =>
  (item && (item.name || item.nazwa || item.nazwa_produktu)) || 'Towar';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [editingPrices, setEditingPrices] = useState({});
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  const [cartDiscountTotal, setCartDiscountTotal] = useState(0);
  const [priceLimitWarnings, setPriceLimitWarnings] = useState({});

  // Oblicz sumę koszyka
  const calculateCartTotal = useCallback(() => {
    const total = cart.reduce((sum, item) => {
      const price = editingPrices[item.id] !== undefined ? editingPrices[item.id] : item.price;
      return sum + Math.round(price * item.quantity * 100) / 100;
    }, 0);
    return Math.round(total * 100) / 100;
  }, [cart, editingPrices]);

  // Oblicz efektywny rabat - JEDNO zrodlo kwoty rabatu w przegladarce.
  //
  // Deleguje do wspolnego silnika rabatu (utils/discountEngine), ktory jest
  // lustrem algorytmu serwera (backend/utils/vat.py): ta sama baza (bez kaucji
  // i pozycji wykluczonych), ten sam clamp do bazy i ta sama twarda regula
  // grosza (zadna pozycja nie moze zejsc ponizej 0.01 zl - decyzja biznesowa
  // wlasciciela 2026-08, patrz naglowek discountEngine.js). Dzieki temu kwota
  // na wyswietlaczu, kwota w walidacji platnosci i discount_amount wyslany do
  // finalizacji to TA SAMA liczba, ktora serwer zapisze w rabat_kwota -
  // koniec z paragonem 3.90 zl przy 31.92 zl zaplaty (transakcja 13292).
  //
  // Rabat liczony jest ZAWSZE od AKTUALNEGO koszyka (ustalenie B wlasciciela):
  // zmiana koszyka po nadaniu rabatu nie zostawia starej kwoty nigdzie.
  // Zamrozone appliedDiscounts[].kwota_rabatu NIE jest tu czytane dla rabatu
  // procentowego - tylko dla kwotowego jako zapasowe zrodlo wartosci.
  const getEffectiveDiscount = useCallback(() => {
    if (appliedDiscounts.length === 0) return 0;

    const discount = appliedDiscounts[0];
    if (!discount) return 0;

    // Pozycje przekazujemy JAWNIE bez pola wartosc_brutto: pozycje zaladowane
    // z serwera (zamowienie/szkic) niosa NIEAKTUALNA migawke wartosc_brutto,
    // a silnik ma liczyc od zywej ceny (editingPrices ma pierwszenstwo).
    const pozycje = cart.map(item => ({
      price: editingPrices[item.id] !== undefined ? editingPrices[item.id] : item.price,
      quantity: item.quantity,
      stawka_vat: item.stawka_vat ?? item.vat,
      typ_produktu: item.typ_produktu,
      is_deposit: item.is_deposit,
      wykluczony_z_rabatow: item.wykluczony_z_rabatow,
    }));

    const rabat = discount.typ_rabatu === 'procentowy'
      ? { typ: 'procentowy', wartosc: parseFloat(discount.wartosc) || 0 }
      : { typ: 'kwotowy', wartosc: parseFloat(discount.wartosc) || parseFloat(discount.kwota_rabatu) || 0 };

    return obliczRabatKoszyka(pozycje, rabat).kwota_rabatu;
  }, [cart, editingPrices, appliedDiscounts]);

  // Oblicz finalną sumę
  const getFinalTotal = useCallback(() => {
    const effectiveDiscount = getEffectiveDiscount();
    return Math.round(Math.max(0, calculateCartTotal() - effectiveDiscount) * 100) / 100;
  }, [calculateCartTotal, getEffectiveDiscount]);

  // Pobierz sumę bez rabatu
  const getTotalAmount = useCallback(() => {
    const total = cart.reduce((sum, item) => {
      return sum + Math.round(item.price * item.quantity * 100) / 100;
    }, 0);
    return Math.round(total * 100) / 100;
  }, [cart]);

  // Usuń z koszyka
  const removeFromCart = useCallback((productId) => {
    const productToRemove = cart.find(item => item.id === productId);

    if (productToRemove && !productToRemove.is_deposit) {
      const depositToRemove = cart.find(item =>
        item.is_deposit && item.deposit_for_product_id === productId
      );

      if (depositToRemove) {
        setCart(prev => prev.filter(item =>
          item.id !== productId && item.id !== depositToRemove.id
        ));
        return;
      }
    }

    setCart(prev => prev.filter(item => item.id !== productId));
  }, [cart]);

  // Aktualizuj ilość
  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const productToUpdate = cart.find(item => item.id === productId);

    if (productToUpdate && !productToUpdate.is_deposit) {
      setCart(prev =>
        prev.map(item => {
          if (item.id === productId) {
            return { ...item, quantity };
          }
          if (item.is_deposit && item.deposit_for_product_id === productId) {
            return { ...item, quantity };
          }
          return item;
        })
      );
    } else {
      setCart(prev =>
        prev.map(item =>
          item.id === productId ? { ...item, quantity } : item
        )
      );
    }
  }, [cart, removeFromCart]);

  // Przełącz wykluczenie z rabatów
  const toggleDiscountExclusion = useCallback((item) => {
    const newValue = !item.wykluczony_z_rabatow;
    setCart(prev =>
      prev.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, wykluczony_z_rabatow: newValue ? 1 : 0 }
          : cartItem
      )
    );
  }, []);

  // Wyczyść koszyk
  const clearCart = useCallback(() => {
    setCart([]);
    setCurrentTransaction(null);
    setAppliedDiscounts([]);
    setCartDiscountTotal(0);
    setEditingPrices({});
    setPriceLimitWarnings({});
  }, []);

  // Resetuj rabaty
  const resetDiscounts = useCallback(() => {
    setAppliedDiscounts([]);
    setCartDiscountTotal(0);
  }, []);

  const value = useMemo(() => ({
    // Stan
    cart,
    setCart,
    editingPrices,
    setEditingPrices,
    currentTransaction,
    setCurrentTransaction,
    appliedDiscounts,
    setAppliedDiscounts,
    cartDiscountTotal,
    setCartDiscountTotal,
    priceLimitWarnings,
    setPriceLimitWarnings,

    // Obliczenia
    calculateCartTotal,
    getEffectiveDiscount,
    getFinalTotal,
    getTotalAmount,

    // Akcje
    removeFromCart,
    updateQuantity,
    toggleDiscountExclusion,
    clearCart,
    resetDiscounts
  }), [
    cart,
    editingPrices,
    currentTransaction,
    appliedDiscounts,
    cartDiscountTotal,
    priceLimitWarnings,
    calculateCartTotal,
    getEffectiveDiscount,
    getFinalTotal,
    getTotalAmount,
    removeFromCart,
    updateQuantity,
    toggleDiscountExclusion,
    clearCart,
    resetDiscounts
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
